import React from 'react';
import { XIcon } from '@phosphor-icons/react';
import { CircularProgress } from '@/components/CircularProgress/CircularProgress';
import { cn } from '@/lib/utils';

export type ChipVariant = 'default' | 'positive' | 'negative';

const variantStyles: Record<ChipVariant, string> = {
    default: 'text-grey-200',
    positive: 'text-green-bright',
    negative: 'text-danger-200',
};

interface ChipProps {
    value: string;
    variant?: ChipVariant;
    icon?: React.ReactNode;
    trailingIcon?: React.ReactNode;
    onClick?: () => void;
    onRemove?: () => void;
    isLoading?: boolean;
    progress?: number;
    maxWidth?: string;
    isSelected?: boolean;
}

export function Chip({
    value,
    variant = 'default',
    icon,
    trailingIcon,
    onClick,
    onRemove,
    isLoading = false,
    progress = 0,
    maxWidth = '200px',
    isSelected = false,
}: ChipProps) {
    return (
        <div
            className={cn(
                'inline-flex justify-between items-center gap-2 pl-1.5 pr-[5px] py-1 h-7 rounded-lg transition-colors',
                isSelected
                    ? 'bg-grey-550 ring-1 ring-grey-200 text-white-100'
                    : `bg-grey-600 ${variantStyles[variant]}`,
                onClick
                    ? 'cursor-pointer hover:text-white-100'
                    : 'cursor-default',
            )}
            style={{ maxWidth }}
            onClick={onClick}
        >
            <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                {(isLoading || icon) && (
                    <span className="flex items-center flex-shrink-0">
                        {isLoading ? (
                            <CircularProgress progress={progress} size={4} />
                        ) : (
                            icon
                        )}
                    </span>
                )}
                <span className="truncate text-sm font-normal leading-5">
                    {value}
                </span>
                {trailingIcon && (
                    <span className="flex items-center flex-shrink-0">
                        {trailingIcon}
                    </span>
                )}
            </div>
            {onRemove && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                    }}
                    className="flex items-center rounded-full p-0.5 transition-colors cursor-pointer hover:text-white-100 flex-shrink-0"
                    aria-label="Remove"
                >
                    <XIcon size={16} />
                </button>
            )}
        </div>
    );
}
